import { Controller } from "@hotwired/stimulus";
import { renderMarkdownToHtml } from "../utils/ai_chat_markdown";

// Streaming chat panel (SSE) for AI assistants embedded in Rails forms.
export default class extends Controller {
  static targets = [
    "scrollRegion",
    "transcript",
    "streamSlot",
    "input",
    "fileInput",
    "attachmentsList",
    "attachButton",
    "composerField",
    "submitButton",
    "submitIconSend",
    "submitIconStop",
    "pendingBlock",
    "thinkingBlock",
    "thinkingBody",
    "thinkingCollapse",
    "thinkingToggle",
    "answerBlock",
    "answerBody",
    "errorBlock",
    "historyMenu",
    "historyList",
    "historyToggle",
  ];

  static values = {
    streamUrl: String,
    context: String,
    contextParams: Object,
    placeholder: String,
    thinkingLabel: String,
    thinkingInProgress: String,
    sendLabel: String,
    stopLabel: String,
    errorGeneric: String,
    draftTitleSelector: String,
    draftBodySelector: String,
    applyDraftUrl: String,
    applyDraftLabel: String,
    applyDraftPending: String,
    applyDraftDone: String,
    applyDraftError: String,
    attachmentsEnabled: Boolean,
    maxAttachments: Number,
    maxAttachmentBytes: Number,
    attachLabel: String,
    removeAttachmentLabel: String,
    attachmentOnlyHistory: String,
    attachmentsTooMany: String,
    attachmentsTooLarge: String,
    attachmentsInvalidType: String,
    historyEnabled: Boolean,
    conversationsUrl: String,
    newConversationLabel: String,
    historyOpenLabel: String,
    todayLabel: String,
    yesterdayLabel: String,
  };

  static PENDING_REVEAL_DELAY_MS = 520;

  static ALLOWED_ATTACHMENT_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

  static DRAFT_MIN_LENGTH = 80;

  static MARKDOWN_HEADING_PATTERN = /^#{1,6}\s+\S/;

  connect() {
    this.history = [];
    this.conversations = [];
    this.conversationId = null;
    this.abortController = null;
    this.pendingRevealTimer = null;
    this.attachments = [];

    if (this.historyEnabledValue) {
      void this.bootstrapConversationHistory();
    }
  }

  disconnect() {
    this.cancelPendingReveal();
    this.clearAttachmentState();
    this.abortStream();
  }

  composerPrimaryAction(event) {
    event?.preventDefault?.();
    if (this.streaming) {
      this.stopStream();
      return;
    }

    this.submit(event);
  }

  submit(event) {
    event?.preventDefault?.();

    const message = this.inputTarget.value.trim();
    const files = this.attachments.map((entry) => entry.file);
    if ((!message && files.length === 0) || this.streaming) return;

    const displayAttachments = [...this.attachments];
    const historyUserContent = message || this.attachmentOnlyHistoryValue;

    this.inputTarget.value = "";
    this.appendUserMessage(message, displayAttachments);
    this.attachments = [];
    this.renderAttachmentList();
    this.startStream(message, files, historyUserContent);
  }

  inputKeydown(event) {
    if (event.key !== "Enter" || event.shiftKey) return;

    event.preventDefault();
    this.submit(event);
  }

  async bootstrapConversationHistory() {
    await this.fetchConversations();
    if (this.conversations.length === 0) return;

    this.showHistoryMenu();
    await this.loadConversation(this.conversations[0].id, { scroll: false });
  }

  conversationsIndexUrl() {
    const url = new URL(this.conversationsUrlValue, window.location.origin);
    url.searchParams.set("context", this.contextValue);
    Object.entries(this.contextParamsValue || {}).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    return url.toString();
  }

  conversationShowUrl(conversationId) {
    return `${this.conversationsUrlValue}/${conversationId}`;
  }

  async fetchConversations() {
    if (!this.historyEnabledValue) return;

    try {
      const response = await fetch(this.conversationsIndexUrl(), {
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
      });
      if (!response.ok) return;

      const payload = await response.json();
      this.conversations = Array.isArray(payload.conversations) ? payload.conversations : [];
      this.renderHistoryDropdown();
      if (this.conversations.length > 0) this.showHistoryMenu();
    } catch (_error) {
      // ignore — history is optional UX
    }
  }

  async loadConversation(conversationId, { scroll = true } = {}) {
    if (!this.historyEnabledValue || !conversationId) return;

    try {
      const response = await fetch(this.conversationShowUrl(conversationId), {
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
      });
      if (!response.ok) return;

      const payload = await response.json();
      this.conversationId = payload.conversation?.id || conversationId;
      this.renderPersistedMessages(Array.isArray(payload.messages) ? payload.messages : []);
      this.renderHistoryDropdown();
      if (scroll) this.scrollToBottom();
    } catch (_error) {
      this.showError(this.errorGenericValue);
    }
  }

  startNewConversation(event) {
    event?.preventDefault?.();
    this.conversationId = null;
    this.history = [];
    this.clearTranscript();
    this.renderHistoryDropdown();
    this.hideError();
  }

  selectConversation(event) {
    event.preventDefault();
    const conversationId = parseInt(event.currentTarget.dataset.conversationId, 10);
    if (Number.isNaN(conversationId)) return;

    void this.loadConversation(conversationId);
  }

  renderPersistedMessages(messages) {
    this.clearTranscript();
    this.history = [];

    messages.forEach((message) => {
      const role = message.role;
      const content = (message.content || "").trim();
      if (!content) return;

      if (role === "user") {
        this.transcriptTarget.appendChild(this.buildMessageNode("user", content));
        this.history.push({ role: "user", content });
        return;
      }

      if (role === "assistant") {
        this.transcriptTarget.appendChild(this.buildAssistantMessageNode(content));
        this.history.push({ role: "assistant", content });
      }
    });
  }

  clearTranscript() {
    if (!this.hasTranscriptTarget) return;
    this.transcriptTarget.replaceChildren();
  }

  showHistoryMenu() {
    if (!this.hasHistoryMenuTarget) return;
    this.historyMenuTarget.classList.remove("d-none");
  }

  renderHistoryDropdown() {
    if (!this.hasHistoryListTarget) return;

    const list = this.historyListTarget;
    list.replaceChildren();

    const newItem = document.createElement("li");
    newItem.className = "ai-chat__history-new";
    const newButton = document.createElement("button");
    newButton.type = "button";
    newButton.className = "dropdown-item";
    newButton.textContent = this.newConversationLabelValue;
    newButton.addEventListener("click", (event) => this.startNewConversation(event));
    newItem.appendChild(newButton);
    list.appendChild(newItem);

    this.conversations.forEach((conversation) => {
      const item = document.createElement("li");
      item.appendChild(this.buildHistoryItem(conversation));
      list.appendChild(item);
    });
  }

  buildHistoryItem(conversation) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "dropdown-item ai-chat__history-item";
    if (conversation.id === this.conversationId) button.classList.add("active");
    button.dataset.conversationId = String(conversation.id);

    // Wrapper: .dropdown-item is a centered flex row theme-wide, the stacking happens inside.
    const body = document.createElement("span");
    body.className = "ai-chat__history-item-body";
    button.appendChild(body);

    const title = document.createElement("span");
    title.className = "ai-chat__history-item-title";
    title.textContent = conversation.title || "";
    body.appendChild(title);

    const formattedDate = this.formatConversationDate(conversation.updated_at);
    if (formattedDate) {
      const meta = document.createElement("span");
      meta.className = "ai-chat__history-item-meta";
      meta.textContent = formattedDate;
      body.appendChild(meta);
    }

    button.addEventListener("click", (event) => this.selectConversation(event));
    return button;
  }

  formatConversationDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    const now = new Date();
    const elapsedDays = this.calendarDaysBetween(date, now);

    if (elapsedDays === 0) return `${this.todayLabelValue} ${time}`;
    if (elapsedDays === 1) return `${this.yesterdayLabelValue} ${time}`;

    const day = date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
    return `${day} · ${time}`;
  }

  calendarDaysBetween(from, to) {
    const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

    return Math.round((startOfDay(to) - startOfDay(from)) / 86_400_000);
  }

  appendUserMessage(text, attachmentEntries = []) {
    const node = this.buildMessageNode("user", text, attachmentEntries);
    this.transcriptTarget.appendChild(node);
    this.scrollToBottom();
  }

  openFilePicker(event) {
    event?.preventDefault?.();
    if (!this.hasFileInputTarget || this.streaming) return;

    this.fileInputTarget.click();
  }

  filesSelected(event) {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    this.ingestAttachmentFiles(selected);
  }

  dragEnter(event) {
    if (!this.allowFileDrop(event)) return;

    event.preventDefault();
    this.setComposerDragover(true);
  }

  dragOver(event) {
    if (!this.allowFileDrop(event)) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    this.setComposerDragover(true);
  }

  dragLeave(event) {
    if (!this.attachmentsEnabledValue) return;

    const related = event.relatedTarget;
    if (related && this.element.contains(related)) return;

    this.setComposerDragover(false);
  }

  dropFiles(event) {
    if (!this.allowFileDrop(event)) return;

    event.preventDefault();
    this.setComposerDragover(false);
    this.ingestAttachmentFiles(Array.from(event.dataTransfer?.files || []));
  }

  allowFileDrop(event) {
    if (!this.attachmentsEnabledValue || this.streaming) return false;

    const types = event.dataTransfer?.types;
    if (!types) return false;

    return Array.from(types).includes("Files");
  }

  setComposerDragover(active) {
    if (!this.hasComposerFieldTarget) return;

    this.composerFieldTarget.classList.toggle("is-dragover", active);
  }

  ingestAttachmentFiles(files) {
    if (!this.attachmentsEnabledValue || this.streaming || files.length === 0) return;

    files.forEach((file) => {
      const error = this.validateAttachmentFile(file);
      if (error) {
        this.showError(error);
        return;
      }

      if (this.attachments.length >= this.maxAttachmentsValue) {
        this.showError(this.attachmentsTooManyValue);
        return;
      }

      this.attachments.push({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
      });
    });

    this.renderAttachmentList();
    this.hideError();
  }

  removeAttachment(event) {
    event.preventDefault();
    const id = event.currentTarget.dataset.attachmentId;
    if (!id) return;

    const entry = this.attachments.find((item) => item.id === id);
    if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);

    this.attachments = this.attachments.filter((item) => item.id !== id);
    this.renderAttachmentList();
  }

  validateAttachmentFile(file) {
    const type = (file.type || "").split(";")[0].trim().toLowerCase();
    if (!this.constructor.ALLOWED_ATTACHMENT_TYPES.includes(type)) {
      return this.attachmentsInvalidTypeValue;
    }

    if (file.size > this.maxAttachmentBytesValue) {
      return this.attachmentsTooLargeValue;
    }

    return null;
  }

  renderAttachmentList() {
    if (!this.hasAttachmentsListTarget) return;

    const list = this.attachmentsListTarget;
    list.replaceChildren();

    if (this.attachments.length === 0) {
      list.classList.add("d-none");
      return;
    }

    list.classList.remove("d-none");

    this.attachments.forEach((entry) => {
      const chip = document.createElement("div");
      chip.className = "ai-chat__composer-attachment";

      const image = document.createElement("img");
      image.src = entry.previewUrl;
      image.alt = "";
      chip.appendChild(image);

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "ai-chat__composer-attachment-remove";
      remove.setAttribute("aria-label", this.removeAttachmentLabelValue);
      remove.dataset.attachmentId = entry.id;
      remove.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
      remove.addEventListener("click", (event) => this.removeAttachment(event));
      chip.appendChild(remove);

      list.appendChild(chip);
    });
  }

  clearAttachmentState() {
    this.attachments.forEach((entry) => {
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    });
    this.attachments = [];
    this.renderAttachmentList();
  }

  async startStream(message, files = [], historyUserContent = message) {
    this.streaming = true;
    this.setSubmitting(true);
    this.thinkingActive = false;
    this.clearAssistantBlocks();
    this.schedulePendingReveal();
    this.hideError();

    this.abortController = new AbortController();
    const formData = new FormData();
    formData.append("context", this.contextValue);
    formData.append("message", message);
    formData.append("history", JSON.stringify(this.history));

    Object.entries(this.contextParamsValue || {}).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const draftTitle = this.readDraftTitle();
    const draftBody = this.readDraftBody();
    if (draftTitle) formData.append("draft_title", draftTitle);
    if (draftBody) formData.append("draft_body", draftBody);
    files.forEach((file) => formData.append("attachments[]", file));

    if (this.historyEnabledValue) {
      formData.append("persist_history", "true");
      if (this.conversationId) {
        formData.append("conversation_id", String(this.conversationId));
      }
    }

    let assistantText = "";
    let thinkingText = "";
    let turnFinalized = false;

    try {
      const response = await fetch(this.streamUrlValue, {
        method: "POST",
        headers: {
          Accept: "text/event-stream",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: formData,
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        const errorMessage = await this.errorMessageFromResponse(response);
        this.hidePending();
        this.showError(errorMessage);
        return;
      }

      if (!response.body) {
        this.hidePending();
        this.showError(this.errorGenericValue);
        return;
      }

      await this.consumeSse(response.body, async (event) => {
        switch (event.type) {
          case "thinking_started":
            this.thinkingActive = true;
            this.showThinkingShell();
            break;
          case "thinking_delta":
            thinkingText += event.text || "";
            this.updateThinking(thinkingText);
            break;
          case "text_delta":
            assistantText += event.text || "";
            this.updateAnswer(assistantText);
            break;
          case "done":
            if (event.conversation_id) this.conversationId = event.conversation_id;
            turnFinalized = this.finalizeTurn(historyUserContent, assistantText || thinkingText);
            if (this.historyEnabledValue) void this.fetchConversations();
            break;
          case "error":
            this.showError(event.message || this.errorGenericValue);
            break;
          default:
            break;
        }

        await this.nextPaint();
      });

      if (!turnFinalized && (assistantText.trim() || thinkingText.trim())) {
        turnFinalized = this.finalizeTurn(historyUserContent, assistantText || thinkingText);
      } else if (!turnFinalized) {
        this.hidePending();
        this.showError(this.errorGenericValue);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        this.clearAssistantBlocks();
      } else {
        this.hidePending();
        this.showError(this.errorGenericValue);
      }
    } finally {
      this.streaming = false;
      this.setSubmitting(false);
      this.abortController = null;
      this.activeStreamReader = null;
    }
  }

  stopStream() {
    if (!this.streaming) return;

    this.activeStreamReader?.cancel().catch(() => {});
    this.abortStream();
  }

  async consumeSse(readableStream, onEvent) {
    const reader = readableStream.getReader();
    this.activeStreamReader = reader;
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";

        for (const chunk of chunks) {
          const dataLine = chunk
            .split("\n")
            .map((line) => line.trim())
            .find((line) => line.startsWith("data:"));
          if (!dataLine) continue;

          try {
            const payload = JSON.parse(dataLine.slice(5).trim());
            await onEvent(payload);
          } catch (_error) {
            // ignore malformed chunks
          }
        }
      }
    } finally {
      if (this.activeStreamReader === reader) {
        this.activeStreamReader = null;
      }
    }
  }

  showThinkingShell() {
    if (!this.hasThinkingBlockTarget) return;

    this.hidePending();

    const block = this.thinkingBlockTarget;
    block.classList.remove("d-none");
    requestAnimationFrame(() => {
      block.classList.add("is-visible");
    });

    if (this.hasThinkingBodyTarget && !this.thinkingBodyTarget.textContent.trim()) {
      this.thinkingBodyTarget.textContent = this.thinkingInProgressValue;
      this.thinkingBodyTarget.dataset.placeholder = "true";
    }

    this.scrollToBottom();
  }

  updateThinking(text) {
    if (!text) return;

    this.showThinkingShell();

    if (this.thinkingBodyTarget.dataset.placeholder === "true") {
      this.thinkingBodyTarget.textContent = "";
      delete this.thinkingBodyTarget.dataset.placeholder;
    }

    this.thinkingBodyTarget.textContent = text;
    this.scrollToBottom();
  }

  updateAnswer(text) {
    if (!text) return;

    this.hidePending();
    if (!this.thinkingActive && this.hasThinkingBlockTarget) {
      this.thinkingBlockTarget.classList.add("d-none");
      this.thinkingBlockTarget.classList.remove("is-visible");
    }
    this.answerBlockTarget.classList.remove("d-none");
    this.answerBodyTarget.innerHTML = this.renderAssistantHtml(text);
    this.scrollToBottom();
  }

  schedulePendingReveal() {
    this.cancelPendingReveal();
    this.pendingRevealTimer = window.setTimeout(() => {
      this.pendingRevealTimer = null;
      this.revealPending();
    }, this.constructor.PENDING_REVEAL_DELAY_MS);
  }

  cancelPendingReveal() {
    if (this.pendingRevealTimer == null) return;

    window.clearTimeout(this.pendingRevealTimer);
    this.pendingRevealTimer = null;
  }

  revealPending() {
    if (!this.hasPendingBlockTarget || !this.streaming) return;

    const block = this.pendingBlockTarget;
    block.classList.remove("d-none");
    requestAnimationFrame(() => {
      block.classList.add("is-visible");
    });
    this.scrollToBottom();
  }

  hidePending() {
    this.cancelPendingReveal();
    if (!this.hasPendingBlockTarget) return;

    const block = this.pendingBlockTarget;
    block.classList.remove("is-visible");
    block.classList.add("d-none");
  }

  finalizeTurn(userMessage, assistantText) {
    const trimmed = assistantText.trim();
    if (!trimmed) return false;

    this.history.push({ role: "user", content: userMessage });
    this.history.push({ role: "assistant", content: trimmed });

    this.transcriptTarget.appendChild(this.buildAssistantMessageNode(trimmed));
    this.clearAssistantBlocks();
    this.scrollToBottom();
    return true;
  }

  buildMessageNode(role, text, attachmentEntries = []) {
    const node = document.createElement("div");
    node.className = `ai-chat-message ai-chat-message--${role} mb-2`;

    const bubble = document.createElement("div");
    bubble.className = "ai-chat-message__bubble";

    if (attachmentEntries.length > 0) {
      const attachments = document.createElement("div");
      attachments.className = "ai-chat-message__attachments";
      attachmentEntries.forEach((entry) => {
        const image = document.createElement("img");
        image.src = entry.previewUrl;
        image.alt = "";
        attachments.appendChild(image);
      });
      bubble.appendChild(attachments);
    }

    if (text) {
      const body = document.createElement("div");
      body.className = "ai-chat-message__text";
      body.textContent = text;
      bubble.appendChild(body);
    }

    node.appendChild(bubble);
    return node;
  }

  buildAssistantMessageNode(text) {
    const node = document.createElement("div");
    node.className = "ai-chat-message ai-chat-message--assistant mb-2";
    node.dataset.markdown = text;

    const content = document.createElement("div");
    content.className = "ai-chat-message__content ai-chat-prose";
    content.innerHTML = this.renderAssistantHtml(text);
    node.appendChild(content);

    const followUp = this.buildMessageFollowUp(text);
    if (followUp) node.appendChild(followUp);

    return node;
  }

  // Context-specific actions shown under assistant replies (extend followUpActionsFor).
  buildMessageFollowUp(text) {
    const actions = this.followUpActionsFor(text);
    if (actions.length === 0) return null;

    const footer = document.createElement("div");
    footer.className = "ai-chat-message__follow-up";
    footer.dataset.aiChatFollowUp = "";

    const row = document.createElement("div");
    row.className = "ai-chat-message__follow-up-actions";

    actions.forEach((action) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = action.buttonClass || "btn btn-primary btn-sm";
      button.textContent = action.label;
      button.dataset.aiChatFollowUpAction = action.id;
      if (action.markdown) button.dataset.markdown = action.markdown;
      button.addEventListener("click", action.onClick);
      row.appendChild(button);
    });

    footer.appendChild(row);
    return footer;
  }

  followUpActionsFor(text) {
    const trimmed = text.trim();
    const actions = [];

    if (this.canApplyDraft(trimmed)) {
      actions.push({
        id: "apply_draft",
        label: this.applyDraftLabelValue,
        markdown: trimmed,
        buttonClass: "btn btn-primary btn-sm",
        onClick: (event) => this.applyDraft(event),
      });
    }

    return actions;
  }

  canApplyDraft(text) {
    return (
      this.contextValue === "academy_article_content" &&
      this.hasApplyDraftUrlValue &&
      this.looksLikeArticleContent(text)
    );
  }

  // Un patch sans titre markdown d'ouverture n'a pas d'ancre de fusion : ApplyChatDraftService
  // écraserait tout l'article avec la réponse. Une explication ou une question n'est donc pas applicable.
  looksLikeArticleContent(text) {
    const trimmed = text.trim();
    if (trimmed.length < this.constructor.DRAFT_MIN_LENGTH) return false;

    const firstLine = trimmed.split("\n").find((line) => line.trim().length > 0) || "";
    return this.constructor.MARKDOWN_HEADING_PATTERN.test(firstLine.trim());
  }

  async applyDraft(event) {
    event.preventDefault();

    const markdown = event.currentTarget.dataset.markdown?.trim();
    if (!markdown || !this.hasApplyDraftUrlValue) return;

    const button = event.currentTarget;
    button.disabled = true;
    button.textContent = this.applyDraftPendingValue;

    try {
      const formData = new FormData();
      formData.append("markdown", markdown);
      const draftBodyJson = this.readDraftBodyJson();
      if (draftBodyJson) formData.append("draft_body_json", draftBodyJson);
      Object.entries(this.contextParamsValue || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch(this.applyDraftUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: formData,
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(Array.isArray(payload.errors) ? payload.errors.join(", ") : this.applyDraftErrorValue);
      }

      this.applyToEditor(payload);
      button.textContent = this.applyDraftDoneValue;
      button.classList.remove("btn-primary");
      button.classList.add("btn-link", "bg-shadow");
    } catch (_error) {
      button.disabled = false;
      button.textContent = this.applyDraftLabelValue;
      this.showError(this.applyDraftErrorValue);
    }
  }

  applyToEditor(payload) {
    const editorElement = document.querySelector('[data-controller~="academy-article-editor"]');
    if (!editorElement) return;

    const editorController = this.application.getControllerForElementAndIdentifier(
      editorElement,
      "academy-article-editor"
    );

    if (editorController) {
      editorController.applyGeneratedContent(payload);
      return;
    }

    editorElement.dispatchEvent(
      new CustomEvent("academy-article-form:content-generated", {
        bubbles: true,
        detail: payload,
      })
    );
  }

  renderAssistantHtml(text) {
    return renderMarkdownToHtml(text);
  }

  async errorMessageFromResponse(response) {
    if (!response.body) return this.errorGenericValue;

    let message = "";
    await this.consumeSse(response.body, async (event) => {
      if (event.type === "error" && event.message) {
        message = event.message;
      }
    });

    return message || this.errorGenericValue;
  }

  clearAssistantBlocks() {
    this.hidePending();
    this.thinkingBlockTarget.classList.add("d-none");
    this.thinkingBlockTarget.classList.remove("is-visible");
    this.thinkingBodyTarget.textContent = "";
    delete this.thinkingBodyTarget.dataset.placeholder;
    this.resetThinkingCollapse();
    this.answerBlockTarget.classList.add("d-none");
    this.answerBodyTarget.innerHTML = "";
  }

  resetThinkingCollapse() {
    if (!this.hasThinkingCollapseTarget) return;

    this.thinkingCollapseTarget.classList.remove("show");
    if (this.hasThinkingToggleTarget) {
      this.thinkingToggleTarget.classList.add("collapsed");
      this.thinkingToggleTarget.setAttribute("aria-expanded", "false");
    }
  }

  showError(message) {
    this.hidePending();
    this.errorBlockTarget.textContent = message;
    this.errorBlockTarget.classList.remove("d-none");
  }

  hideError() {
    this.errorBlockTarget.textContent = "";
    this.errorBlockTarget.classList.add("d-none");
  }

  setSubmitting(active) {
    if (!this.hasSubmitButtonTarget) return;

    this.submitButtonTarget.disabled = false;
    this.submitButtonTarget.classList.toggle("ai-chat__composer-send--stop", active);
    this.submitButtonTarget.setAttribute("aria-label", active ? this.stopLabelValue : this.sendLabelValue);

    if (this.hasSubmitIconSendTarget) {
      this.submitIconSendTarget.classList.toggle("d-none", active);
    }
    if (this.hasSubmitIconStopTarget) {
      this.submitIconStopTarget.classList.toggle("d-none", !active);
    }

    if (this.hasAttachButtonTarget) {
      this.attachButtonTarget.disabled = active;
    }
  }

  scrollToBottom() {
    if (!this.hasScrollRegionTarget) return;

    this.scrollRegionTarget.scrollTop = this.scrollRegionTarget.scrollHeight;
  }

  nextPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  }

  readDraftTitle() {
    if (!this.hasDraftTitleSelectorValue) return "";
    const el = document.querySelector(this.draftTitleSelectorValue);
    return el?.value?.trim() || "";
  }

  readDraftBody() {
    if (!this.hasDraftBodySelectorValue) return "";
    const el = document.querySelector(this.draftBodySelectorValue);
    return el?.value?.trim() || el?.textContent?.trim() || "";
  }

  readDraftBodyJson() {
    const el = document.querySelector('[data-academy-article-editor-target="jsonInput"]');
    return el?.value?.trim() || "";
  }

  abortStream() {
    this.abortController?.abort();
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  escapeHtml(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}
